import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { makeRequest } from "../../utils/request";
import { User } from "./userSlice";
import { Organization } from "./organizationSlice";

type ResetPasswordPayload = {
  email: string;
};

type SignInPayload = {
  email?: string;
  password?: string;
  inviteId?: string;
};

type SignUpPayload = {
  email?: string;
  password?: string;
};

export type SessionState = {
  user: User | null;
  organization: Organization | null; // User's own organization
  selectedOrganization: Organization | null; // Admin-selected override organization
  error: string | null;
  isLoading: boolean;
  isLoadingSession: boolean;
  languageCode?: string;
  selectedOrganizationId?: string; // For admin users to switch organizations
  invite?: {
    id: string;
    email?: string;
  };
};

const initialState: SessionState = {
  user: null,
  organization: null,
  selectedOrganization: null,
  error: null,
  isLoading: false,
  isLoadingSession: true,
  languageCode: "",
  selectedOrganizationId: typeof window !== "undefined" ? localStorage.getItem("selectedOrganizationId") || undefined : undefined,
};

const getSession = createAsyncThunk("session/getSession", async (_, { getState }) => {
  const response = await makeRequest<{ data: User }>("/v1/auth/session");
  const user = response.data || {};

  let organization: Organization | null = null;
  let selectedOrganization: Organization | null = null;

  // Skip org fetch for unverified users (would 403)
  if (user?.emailVerified !== false) {
    // Fetch user's own organization
    const orgResponse = await makeRequest<{ data: Organization }>(`/v1/organizations/${user.organizationId}`);
    organization = orgResponse.data;

    // If admin has a selected org override, fetch that too
    const state = getState() as { session: SessionState };
    const selectedOrgId = state.session.selectedOrganizationId;
    if (user?.isAdmin && selectedOrgId && selectedOrgId !== user.organizationId) {
      try {
        const selectedOrgResponse = await makeRequest<{ data: Organization }>(`/v1/organizations/${selectedOrgId}`);
        selectedOrganization = selectedOrgResponse.data;
      } catch {
        // Selected org no longer accessible — will be cleared by the reducer
      }
    }
  }

  return { user, organization, selectedOrganization };
});

const signIn = createAsyncThunk("session/signin", async (payload: SignInPayload, { dispatch }) => {
  await makeRequest("/v1/auth/signin", {
    method: "POST",
    body: payload,
  });

  await dispatch(getSession());
});

const signUp = createAsyncThunk("session/signup", async (payload: SignUpPayload, { dispatch }) => {
  await makeRequest("/v1/auth/signup", {
    method: "POST",
    body: payload,
  });

  await dispatch(getSession());
});

const signOut = createAsyncThunk("session/signout", async () => {
  await makeRequest("/v1/auth/signout", {
    method: "POST",
  });
});

const resetPassword = createAsyncThunk("session/resetPassword", async (payload: ResetPasswordPayload) => {
  await makeRequest("/v1/auth/reset", {
    method: "POST",
    body: payload,
  });
});

const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    setInvite: (state, action) => {
      state.invite = action.payload;
    },
    setSelectedOrganization: (state, action: { payload: { id: string; organization: Organization } | null }) => {
      if (action.payload) {
        state.selectedOrganizationId = action.payload.id;
        state.selectedOrganization = action.payload.organization;
        localStorage.setItem("selectedOrganizationId", action.payload.id);
      } else {
        state.selectedOrganizationId = undefined;
        state.selectedOrganization = null;
        localStorage.removeItem("selectedOrganizationId");
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSession.fulfilled, (state, action) => {
      state.isLoadingSession = false;
      state.user = action.payload.user;
      state.organization = action.payload.organization;
      state.selectedOrganization = action.payload.selectedOrganization;
      // If admin had a selected org that's no longer accessible, clear it
      if (state.selectedOrganizationId && !action.payload.selectedOrganization && action.payload.user?.isAdmin) {
        state.selectedOrganizationId = undefined;
        localStorage.removeItem("selectedOrganizationId");
      }
    });

    builder.addCase(getSession.rejected, (state) => {
      state.isLoadingSession = false;
      state.user = null;
      state.organization = null;
      state.selectedOrganization = null;
    });

    builder.addCase(signIn.rejected, (state) => {
      state.error = "Invalid email or password";
    });

    builder.addCase(signOut.fulfilled, (state) => {
      state.isLoading = false;
      state.user = null;
      state.organization = null;
      state.selectedOrganization = null;
    });

    builder.addCase(signUp.rejected, (state, action) => {
      state.error = action.error.message || null;
    });

    builder.addMatcher(
      (action) => action.type.startsWith("session/") && action.type.endsWith("/pending"),
      (state) => {
        state.isLoading = true;
        state.error = null;
      },
    );

    builder.addMatcher(
      (action) => action.type.startsWith("session/") && action.type.endsWith("/rejected"),
      (state) => {
        state.isLoading = false;
      },
    );

    builder.addMatcher(
      (action) => action.type.startsWith("session/") && action.type.endsWith("/fulfilled"),
      (state) => {
        state.isLoading = false;
      },
    );
  },
});

export const sessionReducer = sessionSlice.reducer;

export const sessionActions = {
  ...sessionSlice.actions,
  getSession,
  resetPassword,
  signIn,
  signUp,
  signOut,
};
